import { useState, useEffect, useCallback, useRef } from 'react';

import PropTypes from 'prop-types';
import {
	BsTelephone,
	BsXLg,
	BsChevronLeft,
	BsArrowRight,
	BsCalendar3,
	BsEnvelope,
	BsLinkedin,
	BsPencilSquare,
	BsDash,
	BsArrowsAngleExpand,
	BsCheckCircleFill,
	BsChevronDown,
	BsChevronUp,
	BsPlus,
} from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Textarea from '#globalComponents/Forms/Textarea';
import IconButton from '#globalComponents/IconButton/IconButton';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import ProgressBar from '#globalComponents/ProgressBar/ProgressBar';
import { useErrorStore } from '#lib/stores/errorStore';
import { getInitials } from '#lib/textUtils';

import { aircallDial, createActivity } from '../../../api/crmApi';
import { useChannelTypes } from '../../../models/channels/queries';
import { useAircallEverywhereStore } from '../../../stores/aircallEverywhereStore';
import { usePowerDialerStore } from '../../../stores/powerDialerStore';
import { formatEuro } from '../../../utils/formatters';
import { useCallingMode, useAircallEverywhere } from '../../aircallEverywhere/useAircallEverywhere';

// Constants

const NOTE_TEMPLATES = [
	{
		label: 'Erreicht',
		text: '✅ Erreicht. Status: \n• Aktuelle Lösung:\n• Pain Points:\n• Budget:\n• Entscheidung bis:\n• Nächste Schritte:',
	},
	{ label: 'Voicemail', text: '📞 Voicemail hinterlassen. Erwähnt:\n• Grund des Anrufs:\n• Rückruf erbeten an:' },
	{ label: 'Rückruf vereinbart', text: '📅 Rückruf am: ___\n• Uhrzeit: ___\n• Thema:\n• Vorbereitung:' },
	{
		label: 'Discovery',
		text: '🔍 Discovery-Call\n• Unternehmensgröße:\n• Use Case:\n• Aktuelle Lösung:\n• Schmerzen:\n• Budget:\n• Timeline:',
	},
	{ label: 'Demo geplant', text: '🎯 Demo geplant für: ___\n• Teilnehmer:\n• Fokus:\n• Vorbereitung:' },
];

const QUICK_CHIPS = [
	{ label: 'Decision-Maker', text: '\n\n--- Decision-Maker ---\nName: \nRolle: \nEinfluss: ' },
	{ label: 'Budget', text: '\n\n--- Budget ---\nHöhe: \nGenehmigung: \nTimeline: ' },
	{ label: 'Pain Point', text: '\n\n--- Pain Point ---\nProblem: \nAuswirkung: \nDringlichkeit: ' },
	{ label: 'Timeline', text: '\n\n--- Timeline ---\nStart: \nDeadline: \nMeilensteine: ' },
	{ label: 'Stakeholder', text: '\n\n--- Stakeholder ---\nName: \nAbteilung: \nRolle im Prozess: ' },
	{ label: 'Einwand', text: '\n\n--- Einwand ---\nEinwand: \nAntwort: \nStatus: ' },
	{ label: 'Next Step', text: '\n\n--- Next Step ---\nAktion: \nVerantwortlich: \nBis wann: ' },
];

// All possible follow-up channels. Filtered at render time based on active channel types.
// matchSlugs: if any of these channel type slugs is active, show this option.
// null = always show (e.g. meeting is an activity type, not a channel).
const ALL_FOLLOWUP_CHANNELS = [
	{ key: 'call', label: 'Anruf', icon: BsTelephone, matchSlugs: ['phone_manual', 'phone_aircall'] },
	{ key: 'email', label: 'E-Mail', icon: BsEnvelope, matchSlugs: ['email'] },
	{ key: 'linkedin', label: 'LinkedIn', icon: BsLinkedin, matchSlugs: ['linkedin'] },
];

const FOLLOWUP_TIMES = [
	{ key: '1h', label: '+1 Std' },
	{ key: 'tomorrow', label: 'Morgen' },
	{ key: '3d', label: '+3 Tage' },
	{ key: '1w', label: '+1 Woche' },
	{ key: 'custom', label: 'Custom', icon: BsCalendar3 },
];

const GRADIENT_COLORS = [
	'from-primary-500 to-primary-700',
	'from-success-500 to-success-700',
	'from-primary-400 to-primary-600',
	'from-caution-500 to-caution-700',
	'from-warning-500 to-warning-700',
	'from-bgBase-700 to-bgBase-800',
];

// Helpers

function getGradient(name) {
	if (!name) return GRADIENT_COLORS[0];
	let hash = 0;
	for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
	return GRADIENT_COLORS[Math.abs(hash) % GRADIENT_COLORS.length];
}

function makeDefaultSession() {
	return { notes: '', followupChannel: null, followup: null, completed: false };
}

// PowerDialer Component

const PowerDialer = ({ queue, minimized, onMinimize, onRestore, onClose }) => {
	const showError = useErrorStore((s) => s.showError);
	const currentIndex = usePowerDialerStore((s) => s.currentIndex);
	const advanceIndex = usePowerDialerStore((s) => s.next);

	const callingMode = useCallingMode();
	const { dial: dialEverywhere, callState: sdkCallState } = useAircallEverywhere();

	// Active channel types → filter follow-up options
	const { data: channelTypes = [] } = useChannelTypes(false);
	const activeSlugs = new Set(channelTypes.map((ct) => ct.slug));
	const followupChannels = ALL_FOLLOWUP_CHANNELS.filter(
		(ch) => ch.matchSlugs === null || ch.matchSlugs.some((slug) => activeSlugs.has(slug)),
	);

	const [status, setStatus] = useState('idle'); // idle | dialing | active | error
	const [sessions, setSessions] = useState(() => queue.map(() => makeDefaultSession()));
	const [templatesOpen, setTemplatesOpen] = useState(false);
	const [savingNote, setSavingNote] = useState(false);
	const notesRef = useRef(null);

	const current = queue[currentIndex] ?? null;
	const isFinished = currentIndex >= queue.length;
	const session = sessions[currentIndex] ?? makeDefaultSession();
	const completedCount = sessions.filter((s) => s.completed).length;

	// Session updater

	const updateSession = useCallback(
		(patch) => {
			setSessions((prev) => {
				const next = [...prev];
				next[currentIndex] = { ...next[currentIndex], ...patch };
				return next;
			});
		},
		[currentIndex],
	);

	// Dial

	const dial = useCallback(async () => {
		if (!current?.phoneNumber) {
			setStatus('idle');
			advanceIndex();
			return;
		}

		setStatus('dialing');
		try {
			if (callingMode === 'everywhere') {
				useAircallEverywhereStore.getState().setWidgetVisible(true);
				await dialEverywhere(current.phoneNumber);
			} else {
				await aircallDial(current.phoneNumber);
			}
			setStatus('active');
		} catch (err) {
			setStatus('error');
			showError({ error: err, title: 'Anruf fehlgeschlagen' });
		}
	}, [current, showError, advanceIndex, callingMode, dialEverywhere]);

	// Auto-dial on index change
	useEffect(() => {
		if (status === 'idle' && current && !isFinished) {
			dial();
		}
	}, [currentIndex, status, current, isFinished, dial]);

	// Detect call ended via Aircall Everywhere SDK — stay on "ended" until user clicks next.
	// Fire on any transition INTO 'ended' (not only from 'active'), so a refused/missed call
	// that goes ringing → ended without ever being answered is still reflected here.
	const prevSdkCallState = useRef(sdkCallState);
	useEffect(() => {
		if (callingMode !== 'everywhere') return;
		if (prevSdkCallState.current !== 'ended' && sdkCallState === 'ended') {
			setStatus('ended');
		}
		prevSdkCallState.current = sdkCallState;
	}, [sdkCallState, callingMode]);

	// Derived status (synced with SDK in everywhere mode)

	const lastAnswerStatus = useAircallEverywhereStore((s) => s.lastAnswerStatus);

	const effectiveStatus = (() => {
		if (callingMode !== 'everywhere') return status;
		if (sdkCallState === 'ringing_outbound') return 'ringing';
		if (sdkCallState === 'active') return 'active';
		if (sdkCallState === 'ended') return 'ended';
		return status;
	})();

	// Save call activity

	const saveCallActivity = useCallback(async () => {
		const s = sessions[currentIndex];
		const item = queue[currentIndex];
		if (!item?.companyId) return;

		const callSlug = callingMode === 'everywhere' || callingMode === 'desktop' ? 'phone_aircall' : 'phone_manual';
		const channelType = channelTypes.find((ct) => ct.slug === callSlug);

		const answerStatus = useAircallEverywhereStore.getState().lastAnswerStatus;
		const reached =
			answerStatus === 'disconnected' || answerStatus === 'refused'
				? false
				: status === 'ended' || sdkCallState === 'ended'
					? true
					: null;

		setSavingNote(true);
		try {
			await createActivity({
				activityType: 'communication',
				channelTypeId: channelType?.id ?? null,
				title: `Anruf: ${item.contactName || item.companyName || 'Unbekannt'}`,
				body: s.notes?.trim() || null,
				crmCompanyId: item.companyId,
				...(item.dealId && { dealId: item.dealId }),
				status: 'completed',
				completedAt: new Date().toISOString(),
				channelPayload: {
					direction: 'outbound',
					reached,
				},
			});
		} catch (err) {
			showError({ error: err, title: 'Aktivität konnte nicht gespeichert werden' });
		} finally {
			setSavingNote(false);
		}
	}, [sessions, currentIndex, queue, showError, channelTypes, callingMode, sdkCallState, status]);

	// Actions

	const handleNext = async () => {
		updateSession({ completed: true });
		await saveCallActivity();
		setStatus('idle');
		advanceIndex();
	};

	const handleBack = () => {
		if (currentIndex <= 0) return;
		// We go back without saving — the session data persists in state
		const store = usePowerDialerStore.getState();
		usePowerDialerStore.setState({ currentIndex: store.currentIndex - 1 });
		setStatus('idle');
	};

	const handleEndSession = async () => {
		updateSession({ completed: true });
		await saveCallActivity();
		onClose();
	};

	const handleFinish = async () => {
		await saveCallActivity();
		updateSession({ completed: true });
		// Advance past the queue to trigger finished state
		setStatus('idle');
		advanceIndex();
	};

	// Quick chip / template

	const appendToNotes = (text) => {
		updateSession({ notes: session.notes + text });
		// Focus textarea after appending
		setTimeout(() => {
			if (notesRef.current) {
				notesRef.current.focus();
				notesRef.current.scrollTop = notesRef.current.scrollHeight;
			}
		}, 0);
	};

	// Progress

	const progressPct = queue.length > 0 ? (currentIndex / queue.length) * 100 : 0;

	// MINIMIZED STATE

	if (minimized) {
		const displayName = current?.contactName || current?.companyName || 'Unbekannt';
		return (
			<div
				onClick={onRestore}
				className="fixed bottom-4 right-4 z-40 w-[380px] bg-white rounded-card border border-bgBase-500 shadow-elevated cursor-pointer transition duration-200 hover:-translate-y-0.5 hover:border-primary-300 group"
			>
				<div className="px-4 py-3 flex items-center gap-3">
					<div className="relative flex-shrink-0">
						<div className="absolute -top-0.5 -left-0.5 w-3 h-3 bg-success-500 rounded-full animate-ping opacity-50" />
						<div className="w-2 h-2 bg-success-500 rounded-full relative z-10" />
					</div>

					<div
						className={`w-8 h-8 rounded-full bg-gradient-to-br ${getGradient(displayName)} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}
					>
						{getInitials(displayName)}
					</div>

					<div className="min-w-0 flex-1">
						<p className="text-sm font-semibold text-text truncate">{displayName}</p>
						<div className="flex items-center gap-2">
							{current?.companyName && current?.contactName && (
								<span className="text-[10px] text-bgBase-800 truncate">{current.companyName}</span>
							)}
							{current?.stageName && (
								<span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-primary-200 text-primary-600">
									{current.stageName}
								</span>
							)}
						</div>
					</div>

					<span className="text-[10px] font-semibold text-bgBase-800 flex-shrink-0 tabular-nums">
						{Math.min(currentIndex + 1, queue.length)}/{queue.length}
					</span>

					{session.notes.length > 0 && (
						<span className="text-[9px] text-primary-500 flex-shrink-0">
							<BsPencilSquare className="w-3 h-3" />
						</span>
					)}

					<div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
						<IconButton
							icon={BsArrowsAngleExpand}
							label="Erweitern"
							onClick={(e) => {
								e.stopPropagation();
								onRestore();
							}}
							className="h-6 w-6"
						/>
						<IconButton
							icon={BsXLg}
							label="Schließen"
							tone="danger"
							onClick={(e) => {
								e.stopPropagation();
								onClose();
							}}
							className="h-6 w-6"
						/>
					</div>
				</div>

				<div className="h-0.5 bg-bgBase-400 rounded-b-card overflow-hidden">
					<div
						className="h-full bg-primary-500 transition-all duration-300"
						style={{ width: `${progressPct}%` }}
					/>
				</div>
			</div>
		);
	}

	// FINISHED / SUMMARY SCREEN

	if (isFinished) {
		return (
			<Modal isOpen onClose={onClose} size="xl">
				<Panel.Header icon={BsCheckCircleFill} onClose={onClose}>
					PowerCaller abgeschlossen
				</Panel.Header>

				<Panel.Body className="p-8">
					<div className="text-center mb-8">
						<p className="text-6xl font-bold text-text">{completedCount}</p>
						<p className="text-sm text-bgBase-800 mt-1">Anrufe abgeschlossen</p>
					</div>

					<div className="max-w-2xl mx-auto">
						<p className="text-xs font-semibold text-bgBase-800 uppercase tracking-wider mb-3">Übersicht</p>
						<div className="space-y-2 max-h-[280px] overflow-y-auto">
							{queue.map((item, idx) => {
								const s = sessions[idx] ?? makeDefaultSession();
								return (
									<div
										key={item.dealId || idx}
										className="flex items-center gap-3 px-3 py-2 bg-bgBase-300 rounded-card"
									>
										<div
											className={`w-7 h-7 rounded-full bg-gradient-to-br ${getGradient(item.contactName || item.companyName)} flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0`}
										>
											{getInitials(item.contactName || item.companyName)}
										</div>
										<div className="min-w-0 flex-1">
											<p className="text-xs font-medium text-text truncate">
												{item.contactName || item.companyName || 'Unbekannt'}
											</p>
											{item.dealTitle && (
												<p className="text-[10px] text-bgBase-800 truncate">{item.dealTitle}</p>
											)}
										</div>
										{s.notes.length > 0 && (
											<span className="text-[10px] text-bgBase-800">
												{s.notes.length} Zeichen
											</span>
										)}
									</div>
								);
							})}
						</div>
					</div>
				</Panel.Body>

				<Panel.Footer className="justify-end">
					<Button
						variant="outline"
						onClick={() => {
							setSessions(queue.map(() => makeDefaultSession()));
							usePowerDialerStore.setState({ currentIndex: 0 });
							setStatus('idle');
						}}
					>
						Erneut starten
					</Button>
					<Button variant="solid" onClick={onClose}>
						Fertig
					</Button>
				</Panel.Footer>
			</Modal>
		);
	}

	// MAIN MODAL — 2-column layout

	const displayName = current?.contactName || current?.companyName || 'Unbekannt';
	const prob = current?.closingChance !== null ? Math.round(Number(current.closingChance) * 100) : null;

	return (
		<div className="fixed inset-0 z-50 flex items-start justify-start bg-text/40 backdrop-blur-sm pl-8 pt-8">
			<div
				className="bg-white rounded-card shadow-elevated w-full max-w-[1080px] overflow-hidden flex flex-col"
				style={{ maxWidth: 'calc(100vw - 430px)', height: '700px', maxHeight: 'calc(100vh - 136px)' }}
			>
				<div className="px-5 py-3 border-b border-bgBase-500">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3 min-w-0">
							<span className="text-xs font-bold text-text tabular-nums flex-shrink-0">
								{Math.min(currentIndex + 1, queue.length)}/{queue.length}
							</span>
							<ProgressBar value={progressPct} max={100} className="w-24 h-1.5 flex-shrink-0" />
							<span className="text-xs font-semibold text-bgBase-800 flex-shrink-0">PowerCaller</span>
						</div>
						<div className="flex items-center gap-2 flex-shrink-0">
							<span className="text-[10px] font-medium text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full">
								{callingMode === 'everywhere' ? 'via Aircall Browser' : 'via Aircall'}
							</span>
							<IconButton icon={BsDash} label="Minimieren" onClick={onMinimize} />
							<IconButton icon={BsXLg} label="Schließen" onClick={handleEndSession} />
						</div>
					</div>
				</div>

				<div className="flex-1 grid grid-cols-[380px_1fr] overflow-hidden">
					<div className="bg-bgBase-300 border-r border-bgBase-500 overflow-y-auto p-5 flex flex-col gap-5">
						<div className="flex items-center gap-3">
							<div
								className={`w-11 h-11 rounded-full bg-gradient-to-br ${getGradient(displayName)} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}
							>
								{getInitials(displayName)}
							</div>
							<div className="min-w-0">
								<p className="text-sm font-semibold text-text truncate">{displayName}</p>
								{current?.contactName && current?.companyName && (
									<p className="text-xs text-bgBase-800 truncate">{current.companyName}</p>
								)}
								<a
									href={`tel:${current?.phoneNumber}`}
									className="text-xs text-primary-600 font-mono hover:underline"
									onClick={(e) => e.stopPropagation()}
								>
									{current?.phoneNumber || 'Keine Telefonnummer'}
								</a>
							</div>
						</div>

						{(current?.dealTitle || current?.dealValue !== null) && (
							<Card className="p-3">
								<div className="flex items-center gap-2 mb-1.5 flex-wrap">
									{current?.stageName && (
										<span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary-200 text-primary-600">
											{current.stageName}
										</span>
									)}
									{current?.dealValue !== null && (
										<span className="text-xs font-semibold text-text">
											{formatEuro(current.dealValue)}
										</span>
									)}
								</div>
								{current?.dealTitle && (
									<p className="text-xs text-text truncate">{current.dealTitle}</p>
								)}
								<div className="flex items-center gap-2 mt-1.5">
									{current?.ownerName && (
										<div
											className="w-5 h-5 rounded-full bg-bgBase-500 flex items-center justify-center text-[8px] font-semibold text-text"
											title={current.ownerName}
										>
											{getInitials(current.ownerName)}
										</div>
									)}
									{prob !== null && (
										<span className="text-[10px] text-bgBase-800">{prob}% Chance</span>
									)}
								</div>
							</Card>
						)}

						<div className="bg-primary-100 border-l-[3px] border-l-primary-400 rounded-r-card px-3 py-2.5">
							<p className="text-[11px] text-primary-700 leading-relaxed">
								{callingMode === 'everywhere'
									? 'Anruf wird direkt im Browser über Aircall Everywhere geführt.'
									: 'Anruf wird über Aircall geführt. Annahme und Auflegen erfolgen in der Aircall-App.'}
							</p>
						</div>

						<div className="flex items-center gap-2">
							{effectiveStatus === 'dialing' && (
								<span className="flex items-center gap-1.5 text-xs font-medium text-primary-600">
									<div className="animate-spin h-3 w-3 border-2 border-primary-600 border-t-transparent rounded-full" />
									Wird gewählt…
								</span>
							)}
							{effectiveStatus === 'ringing' && (
								<span className="flex items-center gap-1.5 text-xs font-medium text-caution-700">
									<div className="w-2 h-2 bg-caution-500 rounded-full animate-pulse" />
									Klingelt…
								</span>
							)}
							{effectiveStatus === 'active' && (
								<span className="flex items-center gap-1.5 text-xs font-medium text-success-700">
									<div className="w-2 h-2 bg-success-500 rounded-full animate-pulse" />
									Anruf aktiv
								</span>
							)}
							{effectiveStatus === 'ended' && (
								<span className="flex items-center gap-1.5 text-xs font-medium text-bgBase-800">
									<div className="w-2 h-2 bg-bgBase-600 rounded-full" />
									{lastAnswerStatus === 'refused' || lastAnswerStatus === 'disconnected'
										? 'Nicht erreicht'
										: 'Anruf beendet'}
								</span>
							)}
							{effectiveStatus === 'error' && (
								<span className="text-xs font-medium text-warning-700">Fehler beim Wählen</span>
							)}
						</div>

						<div>
							<p className="text-[10px] font-semibold text-bgBase-800 uppercase tracking-wider mb-2">
								Follow-up planen
							</p>
							<div className="grid grid-cols-4 gap-1.5 mb-2">
								{followupChannels.map((ch) => {
									const Icon = ch.icon;
									const isActive = session.followupChannel === ch.key;
									return (
										<button
											key={ch.key}
											onClick={() => updateSession({ followupChannel: isActive ? null : ch.key })}
											className={`flex flex-col items-center gap-0.5 px-1.5 py-2 rounded-control border text-[9px] font-medium transition-colors ${
												isActive
													? 'border-primary-500 bg-primary-100 text-primary-700'
													: 'border-bgBase-500 text-bgBase-800 hover:border-primary-300 hover:text-primary-600'
											}`}
										>
											<Icon className="w-3 h-3" />
											{ch.label}
										</button>
									);
								})}
							</div>
							<div className="flex flex-wrap gap-1.5">
								{FOLLOWUP_TIMES.map((t) => {
									const isActive = session.followup === t.key;
									return (
										<button
											key={t.key}
											onClick={() => updateSession({ followup: isActive ? null : t.key })}
											className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors ${
												isActive
													? 'bg-primary-200 text-primary-700 border border-primary-300'
													: 'bg-bgBase-400 text-bgBase-800 hover:bg-bgBase-500 border border-transparent'
											}`}
										>
											{t.icon && <t.icon className="w-2.5 h-2.5" />}
											{t.label}
										</button>
									);
								})}
							</div>
						</div>
					</div>

					<div className="bg-white overflow-y-auto p-5 flex flex-col">
						<div className="flex items-center justify-between mb-3">
							<div>
								<p className="text-sm font-semibold text-text">Notizen</p>
								<p className="text-[10px] text-bgBase-800">Werden automatisch zum Deal gespeichert</p>
							</div>
							{session.notes.length > 0 && (
								<span className="text-[10px] text-success-700 font-medium flex items-center gap-1">
									<BsCheckCircleFill className="w-2.5 h-2.5" /> Auto-gespeichert
								</span>
							)}
						</div>

						<div className="flex flex-wrap gap-1.5 mb-3">
							{QUICK_CHIPS.map((chip) => (
								<button
									key={chip.label}
									onClick={() => appendToNotes(chip.text)}
									className="inline-flex items-center gap-0.5 px-2 py-1 rounded-full bg-primary-100 text-primary-600 text-[10px] font-medium hover:bg-primary-200 transition-colors"
								>
									<BsPlus className="w-3 h-3" /> {chip.label}
								</button>
							))}
						</div>

						<div className="mb-3">
							<button
								onClick={() => setTemplatesOpen(!templatesOpen)}
								className="flex items-center gap-1.5 text-[11px] font-medium text-bgBase-800 hover:text-text transition-colors"
							>
								{templatesOpen ? (
									<BsChevronUp className="w-2.5 h-2.5" />
								) : (
									<BsChevronDown className="w-2.5 h-2.5" />
								)}
								Vorlage einfügen
							</button>
							{templatesOpen && (
								<div className="mt-1.5 flex flex-wrap gap-1.5">
									{NOTE_TEMPLATES.map((tpl) => (
										<button
											key={tpl.label}
											onClick={() => {
												appendToNotes('\n\n' + tpl.text);
												setTemplatesOpen(false);
											}}
											className="px-2.5 py-1 text-[10px] font-medium text-text bg-bgBase-400 hover:bg-bgBase-500 rounded-control transition-colors"
										>
											{tpl.label}
										</button>
									))}
								</div>
							)}
						</div>

						<Textarea
							ref={notesRef}
							value={session.notes}
							onChange={(e) => updateSession({ notes: e.target.value })}
							placeholder={
								'Notizen zum Gespräch...\n\n• Was wurde besprochen?\n• Welche Einwände gab es?\n• Nächste Schritte?'
							}
							className="flex-1 min-h-[200px] !resize-none"
							autoFocus
						/>

						<div className="flex items-center justify-between mt-2">
							<span className="text-[10px] text-bgBase-800 tabular-nums">
								{session.notes.length} Zeichen
							</span>
							{current?.dealTitle && (
								<span className="text-[10px] text-bgBase-800 truncate max-w-[200px]">
									Deal: {current.dealTitle}
								</span>
							)}
						</div>
					</div>
				</div>

				<div className="px-5 py-3 border-t border-bgBase-500 flex items-center justify-between">
					<div className="flex items-center gap-4">
						<Button onClick={handleBack} disabled={currentIndex <= 0} variant="outline" size="sm">
							<BsChevronLeft className="w-3 h-3" /> Zurück
						</Button>

						<span className="text-[10px] text-bgBase-800">
							{completedCount} von {queue.length} abgeschlossen
						</span>
					</div>

					<div className="flex items-center gap-2">
						<Button
							onClick={currentIndex >= queue.length - 1 ? handleFinish : handleNext}
							disabled={effectiveStatus === 'active' || effectiveStatus === 'ringing'}
							loading={savingNote}
							size="sm"
						>
							{currentIndex >= queue.length - 1 ? 'Abschließen' : 'Nächster Anruf'}
							{currentIndex < queue.length - 1 && <BsArrowRight className="w-3 h-3" />}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

PowerDialer.propTypes = {
	queue: PropTypes.arrayOf(
		PropTypes.shape({
			dealId: PropTypes.string,
			companyName: PropTypes.string,
			contactName: PropTypes.string,
			phoneNumber: PropTypes.string,
			companyId: PropTypes.string,
			dealTitle: PropTypes.string,
			dealValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			closingChance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			stageName: PropTypes.string,
			ownerName: PropTypes.string,
		}),
	).isRequired,
	minimized: PropTypes.bool,
	onMinimize: PropTypes.func.isRequired,
	onRestore: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
	onOpenDeal: PropTypes.func,
};

export default PowerDialer;
